import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NEVER_QUEUED, sendBillingMutation } from '../../lib/billing-mutations';
import { useBillingApi } from '../../billing-host/api';

/**
 * Client API de l'arbitrage des signatures MCF en doute.
 *
 * Une ligne « en doute » est une signature dont la commande a été ÉMISE
 * au MCF sans que son issue soit connue : le compteur fiscal a peut-être
 * été brûlé, peut-être pas. Elle bloque sa facture ou sa vente et ne
 * s'auto-résout JAMAIS — la sortie est une DÉCLARATION humaine, prise
 * après lecture du rapport X/Z du périphérique.
 *
 * GET  /api/mcf/signatures-en-doute
 * POST /api/mcf/signatures-en-doute/:journalId/abandon     (le MCF n'a pas signé)
 * POST /api/mcf/signatures-en-doute/:journalId/signature   (le MCF a signé)
 * POST /api/mcf/signatures-en-doute/:journalId/serie       (series of a signed row naming no site)
 * POST /api/mcf/signatures-en-doute/:journalId/regularisation (late certification of a number issued without the MCF)
 *
 * Permission : `mcf:arbitrer-signature`, portée par le Gérant — le seul
 * rôle livré qui a accès PHYSIQUE au MCF et à son rapport X/Z. Elle vit
 * dans sa propre maison `mcf:` pour que le joker `invoices:*` ne
 * l'accorde jamais par ricochet.
 *
 * ★ AUCUNE ACTION DE MASSE : chaque ligne est une décision fiscale
 * distincte, et l'API ne propose aucun point d'entrée qui en résoudrait
 * plusieurs.
 */

/** A contradicting MCF report kept on a row, with its attempt, outcome, reception date and sender. */
export interface RapportMcfConserve {
  tentative: string;
  issue: 'SIGNED' | 'ABORTED' | 'PENDING';
  /**
   * The enrolled agent that sent it; null for a report of this server's own
   * certifier, or one filed before senders were recorded.
   */
  agentId: string | null;
  /** That agent's name in this dossier; null when `agentId` is null or names no agent of the dossier. */
  agentNom: string | null;
  /** ISO date the server received it. */
  recuLe: string;
}

/** Une signature dont l'issue MCF est inconnue. */
export interface SignatureEnDoute {
  journalId: string;
  /**
   * `INVOICE`: an invoice exists · `POS`: the sale did not complete ·
   * `AGENT`: a site agent reported an attempt naming no document.
   */
  origin: 'INVOICE' | 'POS' | 'AGENT';
  invoiceId: string | null;
  posTransactionId: string | null;
  /** Number to match against the X/Z report; null when none is known. */
  invoiceNumber: string | null;
  /** Decimal Prisma : arrive en chaîne. */
  totalTTC: string | null;
  /** Stored currency of the invoice or sale, the currency of `totalTTC`; null when none is stored. */
  currency: string | null;
  invoiceType: string | null;
  /** Counters C0h reserved and the MID of an annex attempt; null on a legacy one. */
  annexe: {
    machineId: string | null;
    typeCounter: number;
    totalCounter: number;
    invoiceType: string | null;
  } | null;
  /**
   * PENDING: outcome unknown, a decision is expected. SIGNED or APPLIED: the
   * MCF signed and a contradicting report is kept on the row; while
   * `documentEmis` is false its certification is applied to no document.
   */
  statut: string;
  /** Whether the row's document is issued; null when the row has none. */
  documentEmis: boolean | null;
  /** Contradicting MCF reports kept on the row. */
  conflits: number;
  /** Those reports, in the order the row keeps them. */
  rapports: RapportMcfConserve[];
  /**
   * Set on a row that presented a number and names no site. A PENDING row is
   * abandoned or confirmed with a series (`SerieDuNumero`); a SIGNED one takes
   * it through `useChooseSeries`. `etablissements` are the sites whose series
   * renders the number. Null when no choice applies.
   */
  serieAChoisir: { etablissements: Array<{ id: string; code: string; name: string }> } | null;
  /**
   * Set when the number the MCF certified is carried by an invoice issued
   * without the MCF in the same series. PENDING: the signature is confirmed
   * first; SIGNED: `useRegulariseSignature` closes it.
   */
  certificationTardive: {
    numeroCertifie: string;
    factureOrdinaire: { id: string; numero: string; emiseLe: string | null };
  } | null;
  /**
   * Set when the document was issued without the MCF under another number
   * (`documentEmis`) and no invoice carries the number the MCF certified for
   * it. PENDING: the signature is confirmed first; SIGNED:
   * `useRegulariseSignature` closes it and retires the certified number.
   */
  certificationSansFacture: {
    numeroCertifie: string;
    documentEmis: { id: string; numero: string; emiseLe: string | null };
  } | null;
  /**
   * Set on an APPLIED row whose late report certifies a number no invoice
   * carries, the document being certified by the MCF under another number
   * (`documentCertifie`): `useRegulariseSignature` closes it and retires the
   * certified number.
   */
  certificationSansFacturePieceCertifiee: {
    numeroCertifie: string;
    documentCertifie: { id: string; numero: string; emiseLe: string | null };
  } | null;
  /**
   * Set when the number the MCF certified for this row is carried by an
   * invoice under another certification of the MCF (`facture`; `compteurs`,
   * the carrier's first): the number is certified twice.
   * `useRegulariseSignature` closes it, numbering untouched.
   */
  certificationDeuxFois: {
    numeroCertifie: string;
    compteurs: number[];
    /** The invoice bears this machine and counter without the ledger's security code. */
    codeAbsentDeLaFacture: boolean;
    facture: { id: string; numero: string; emiseLe: string | null };
  } | null;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DecisionResult {
  journalId: string;
  status: 'ABORTED' | 'SIGNED';
}

/**
 * Result of a confirmation. `serieAChoisir`: the row stays SIGNED with a
 * number and no site, and its series is chosen next (`useChooseSeries`); a
 * till sale replays only after that.
 */
export interface ConfirmationResult extends DecisionResult {
  serieAChoisir: boolean;
}

/** Result of a regularisation: the row is closed APPLIED. */
export interface RegularisationResult {
  journalId: string;
  status: 'APPLIED';
}

export const mcfSignatureKeys = {
  all: ['mcf-signatures'] as const,
  enDoute: () => [...mcfSignatureKeys.all, 'en-doute'] as const,
};

/**
 * La file d'arbitrage du dossier.
 *
 * `enabled` est HONORÉ comme react-query l'entend : la barre latérale
 * n'interroge cette route que pour un porteur de la permission, et un
 * dossier sain reçoit `[]` — c'est cette liste vide qui retire l'entrée
 * de menu (zéro churn pour les dossiers sans divergence).
 */
export function useSignaturesEnDoute(options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: mcfSignatureKeys.enDoute(),
    queryFn: () =>
      api.get<{ signatures: SignatureEnDoute[] }>('/mcf/signatures-en-doute'),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60,
  });
}

function useArbitrageMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mcfSignatureKeys.all });
      // La décision change l'état de la pièce (re-signable ou en attente
      // de reprise) : la liste des factures doit être relue.
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * DÉCISION 1 — le MCF n'a PAS signé (constaté au rapport X/Z).
 * La pièce redevient signable. Si le MCF avait signé en réalité, ce
 * choix conduit à un SECOND compteur fiscal : d'où l'acquittement.
 */
export function useAbandonSignature() {
  const api = useBillingApi();
  return useArbitrageMutation(
    ({ journalId, motif, serie }: { journalId: string; motif: string; serie?: SerieDuNumero }) =>
      sendBillingMutation<DecisionResult>(api, 'POST', `/mcf/signatures-en-doute/${journalId}/abandon`, {
        acquittement: true,
        motif,
        ...(serie !== undefined ? { serie } : {}),
      })
  );
}

/** Series of a number presented by a row naming no site: a site to retire it in, or none. */
export type SerieDuNumero = { etablissementId: string } | { aucune: true };

export interface ConfirmSignatureInput {
  journalId: string;
  motif: string;
  /** Identifiant machine imprimé : `NIM-(ACNT)`. */
  nim: string;
  /** Code SECeF (16 caractères). */
  securityCode: string;
  /** Donnée QR BRUTE (`BFSECEF;…`, 10 champs). */
  qrData: string;
  /** Numéro de la facture DANS le MCF. */
  mcfCounter: number;
  /** FC, with the annex QR (`BFSECEF01;MID;SIG;IFU;DT`). */
  typeCounter?: number;
  /** FT, with the annex QR. */
  invoiceType?: string;
  /** REQUIS pour une vente POS : la facture n'existe pas encore. */
  invoiceNumber?: string;
  /** Required on a row naming no site (`serieAChoisir`); a till sale takes a site. */
  serie?: SerieDuNumero;
}

/**
 * DÉCISION 2 — le MCF A signé ; voici les éléments relevés.
 * Le balayage de reprise ré-applique tout seul, SANS toucher au
 * périphérique. Si le MCF n'avait pas signé, ce choix certifie une pièce
 * avec des éléments qui n'existent pas.
 */
export function useConfirmSignature() {
  const api = useBillingApi();
  return useArbitrageMutation(({ journalId, ...body }: ConfirmSignatureInput) =>
    sendBillingMutation<ConfirmationResult>(api, 'POST', `/mcf/signatures-en-doute/${journalId}/signature`, {
      acquittement: true,
      ...body,
    })
  );
}

/**
 * The series of a SIGNED row naming no site: until it is chosen, every draw
 * reaching its number is refused.
 */
export function useChooseSeries() {
  const api = useBillingApi();
  return useArbitrageMutation(
    ({ journalId, motif, serie }: { journalId: string; motif: string; serie: SerieDuNumero }) =>
      sendBillingMutation<DecisionResult>(api, 'POST', `/mcf/signatures-en-doute/${journalId}/serie`, {
        acquittement: true,
        motif,
        serie,
      })
  );
}

/**
 * Closes a SIGNED row whose certified number an invoice issued without the
 * MCF carries. The DGI-side declaration stays the operator's.
 */
export function useRegulariseSignature() {
  const api = useBillingApi();
  return useArbitrageMutation(({ journalId, motif }: { journalId: string; motif: string }) =>
    sendBillingMutation<RegularisationResult>(api, 'POST', `/mcf/signatures-en-doute/${journalId}/regularisation`, {
      acquittement: true,
      motif,
    })
  );
}
